/**
 * Notification Page Registry
 *
 * Single source of truth for all per-page notification configuration.
 * Each entry describes a page that can generate or receive notifications.
 *
 * To add a new notification page:
 * 1. Add an entry to NOTIFICATION_PAGE_REGISTRY below
 * 2. The NotificationPageId type, NOTIFICATION_PAGES array, and buildPageRecord
 *    all update automatically — no other files need to change
 */

/** Groups for organizing notification pages in the settings UI (matches departments) */
export type NotificationPageGroup = 'rental' | 'parts' | 'supplies';

/** Ordered display metadata for each notification page group */
const NOTIFICATION_PAGE_GROUP_ORDER: NotificationPageGroup[] = ['rental', 'parts', 'supplies'];

export const NOTIFICATION_PAGE_GROUPS: Record<NotificationPageGroup, { label: string; icon: string }> = {
  rental: { label: 'Rental', icon: 'fa-truck-pickup' },
  parts: { label: 'Parts', icon: 'fa-boxes-stacked' },
  supplies: { label: 'Supplies', icon: 'fa-boxes-stacked' },
};

/** Shape of each entry in the notification page registry (used for satisfies validation) */
interface NotificationPageRegistryEntry {
  label: string;
  description: string;
  icon: string;
  color: string;
  group: NotificationPageGroup;
  pagePermissionKey: string;
  comingSoon?: boolean;
  /** Legacy v2 category names that mapped to this page (for migration) */
  legacyCategories: readonly string[];
}

/**
 * Central registry of all notification pages.
 *
 * Adding a key here automatically:
 * - Adds it to the `NotificationPageId` union
 * - Includes it in `NOTIFICATION_PAGES` and `NOTIFICATION_PAGE_IDS`
 * - Makes `buildPageRecord` generate an entry for it
 */
export const NOTIFICATION_PAGE_REGISTRY = {
  rentalBoard: {
    label: 'Rental Board',
    description: 'Returns, swaps, prep completions, and board updates',
    icon: 'fa-truck-pickup',
    color: 'orange',
    group: 'rental',
    pagePermissionKey: 'rentals',
    legacyCategories: ['sales'],
  },
  yard: {
    label: 'Yard Ops',
    description: 'Prep requests for yard crew',
    icon: 'fa-warehouse',
    color: 'orange',
    group: 'rental',
    pagePermissionKey: 'yard',
    legacyCategories: ['yard'],
  },
  truckingCalendar: {
    label: 'Trucking Calendar',
    description: 'Reminders, scheduled events, and status changes',
    icon: 'fa-truck',
    color: 'orange',
    group: 'rental',
    pagePermissionKey: 'truckingCalendar',
    legacyCategories: [],
  },
} as const satisfies Record<string, NotificationPageRegistryEntry>;

/** Derived union of all notification page ID strings */
export type NotificationPageId = keyof typeof NOTIFICATION_PAGE_REGISTRY;

/** Array of all notification page IDs (for iteration) */
export const NOTIFICATION_PAGE_IDS = Object.keys(NOTIFICATION_PAGE_REGISTRY) as NotificationPageId[];

/** Configuration shape for a notification page (derived from registry) */
export interface NotificationPageConfig {
  id: NotificationPageId;
  label: string;
  description: string;
  icon: string;
  color: string;
  group: NotificationPageGroup;
  pagePermissionKey: string;
  comingSoon: boolean;
  legacyCategories: readonly string[];
}

/** Array of all notification page configs (for iteration in UI components) */
export const NOTIFICATION_PAGES: NotificationPageConfig[] = NOTIFICATION_PAGE_IDS.map((id) => {
  const entry: NotificationPageRegistryEntry = NOTIFICATION_PAGE_REGISTRY[id];
  return {
    id,
    label: entry.label,
    description: entry.description,
    icon: entry.icon,
    color: entry.color,
    group: entry.group,
    pagePermissionKey: entry.pagePermissionKey,
    comingSoon: entry.comingSoon ?? false,
    legacyCategories: entry.legacyCategories,
  };
});

/** Pages grouped by department, in display order (only groups with pages are included) */
export const NOTIFICATION_PAGES_GROUPED: { group: NotificationPageGroup; pages: NotificationPageConfig[] }[] =
  NOTIFICATION_PAGE_GROUP_ORDER
    .map((group) => ({
      group,
      pages: NOTIFICATION_PAGES.filter((p) => p.group === group),
    }))
    .filter(({ pages }) => pages.length > 0);

/**
 * Build a Record<NotificationPageId, T> with a value generated by a factory function.
 *
 * Eliminates manually-maintained Record objects that must list all page IDs.
 * The factory is called once per page to avoid shared references.
 *
 * @example
 * // Empty arrays for all pages
 * const emptyRecord = buildPageRecord<string[]>(() => []);
 *
 * // Same value for all pages
 * const allLocations = buildPageRecord<string[]>(() => [...locationIds]);
 */
export function buildPageRecord<T>(factory: () => T): Record<NotificationPageId, T> {
  const result = {} as Record<NotificationPageId, T>;
  for (const id of NOTIFICATION_PAGE_IDS) {
    result[id] = factory();
  }
  return result;
}
